import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'ourora.analytics.projection.v1';
const TAB_KEY = 'ourora.analytics.projection.tab.v1';
const CHANNEL_NAME = 'ourora.analytics.projection.tabs.v1';

interface StoredProjection {
  tabId: string;
  userId: string;
  percentage: number;
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function readStoredProjection(): StoredProjection | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    if (!value) return null;
    const parsed = JSON.parse(value) as StoredProjection;
    if (
      typeof parsed.tabId !== 'string' ||
      typeof parsed.userId !== 'string' ||
      !Number.isFinite(parsed.percentage) ||
      parsed.percentage <= 0
    ) return null;
    return parsed;
  } catch {
    return null;
  }
}

function navigationType(): string | null {
  if (typeof performance.getEntriesByType !== 'function') return null;
  const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return entry?.type || null;
}

async function establishTabIdentity(): Promise<string> {
  let tabId = sessionStorage.getItem(TAB_KEY);
  if (!tabId) {
    tabId = randomId();
    sessionStorage.setItem(TAB_KEY, tabId);
    return tabId;
  }

  // The responder survives Analytics route unmounts in this document. A matching
  // identity therefore means same-tab navigation, not a cloned tab.
  if (responderTabId === tabId) return tabId;
  if (navigationType() === 'reload' || typeof BroadcastChannel === 'undefined') return tabId;

  const channel = new BroadcastChannel(CHANNEL_NAME);
  const probeId = randomId();
  const duplicate = await new Promise<boolean>((resolve) => {
    let settled = false;
    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      channel.removeEventListener('message', onMessage);
      resolve(value);
    };
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'alive' && event.data?.probeId === probeId) finish(true);
    };
    channel.addEventListener('message', onMessage);
    channel.postMessage({ type: 'probe', tabId, probeId });
    window.setTimeout(() => finish(false), 100);
  });
  channel.close();

  if (!duplicate) return tabId;
  tabId = randomId();
  sessionStorage.setItem(TAB_KEY, tabId);
  sessionStorage.removeItem(STORAGE_KEY);
  return tabId;
}

let responderChannel: BroadcastChannel | null = null;
let responderTabId: string | null = null;

function startDuplicateResponder(tabId: string) {
  if (typeof BroadcastChannel === 'undefined') return;
  if (responderChannel && responderTabId === tabId) return;
  responderChannel?.close();
  responderTabId = tabId;
  responderChannel = new BroadcastChannel(CHANNEL_NAME);
  responderChannel.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type === 'probe' && event.data?.tabId === responderTabId) {
      responderChannel?.postMessage({ type: 'alive', probeId: event.data.probeId });
    }
  });
}

export function clearAnalyticsProjectionSession() {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function useAnalyticsProjection(userId: string | null, isAdmin: boolean) {
  const [percentage, setPercentageState] = useState<number | null>(null);
  const [ready, setReady] = useState(false);
  const [tabId, setTabId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!userId || !isAdmin) {
      clearAnalyticsProjectionSession();
      setPercentageState(null);
      setReady(true);
      return () => { cancelled = true; };
    }

    setReady(false);
    establishTabIdentity().then((identity) => {
      if (cancelled) return;
      startDuplicateResponder(identity);
      setTabId(identity);
      const stored = readStoredProjection();
      if (stored && (stored.tabId !== identity || stored.userId !== userId)) {
        clearAnalyticsProjectionSession();
      }
      setPercentageState(
        stored && stored.tabId === identity && stored.userId === userId ? stored.percentage : null,
      );
      setReady(true);
    });
    return () => { cancelled = true; };
  }, [isAdmin, userId]);

  const setPercentage = useCallback((value: number | null) => {
    if (!isAdmin || !userId || !tabId || value === null || value === 0) {
      clearAnalyticsProjectionSession();
      setPercentageState(null);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ tabId, userId, percentage: value }));
    setPercentageState(value);
  }, [isAdmin, tabId, userId]);

  return { percentage, ready, setPercentage };
}
